/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as types from '../constants';
import {
    addUiFilter,
    createEmptyUiFilters,
    removeUiFilter,
    removeDisallowedActivityTags,
    setUiFilter,
} from '../utils/filters';

function cloneState(state) {
    return JSON.parse(JSON.stringify(state));
}

const INITIAL_STATE = {
    what: '',
    headerTextQuery: '',
    where: '',
    lat: null,
    lng: null,
    map_center_lat: null,
    map_center_lng: null,
    radius: null,
    placeid: null,
    placename: null,
    start: null,
    sort: 'score',
    size: null,
    checkin_time: null,
    checkout_time: null,
    uiFilters: {
        hasFilters: false,
        accommodations: {},
        agencies: {},
        campsiteUseTypes: {},
        campsiteTypes: {},
        campsiteAmenities: {},
        campsiteElectricityHookup: {},
        campsiteVehicleLength: {},
        campsiteEquipment: {},
        activities: {},
        activityTags: {},
        occupants: {},
        inventoryTypes: {},
        tourTypes: {},
        tourAccessible: {},
        tourDifficulty: {},
        tourDuration: {},
        tourPrice: {},
        tourTime: {},
        tourTimes: {},
        permitTypes: {},
        reservable: {},
        lastAppliedFilter: null,
        includeUnavailable: true,
    },
    inventoryTypeFiltersVisible: false,
    filtersVisible: false,
    filters: [],
    aggregations: [],
};

// Store the persistent state props
const setStorage = (state) => {
    let st = state;
    if (!st) {
        st = {};
    }
    st.last_updated_datetime = new Date().getTime();
    if (window.localStorage) {
        try {
            window.localStorage.setItem('r1s_search_session', JSON.stringify(st));
        } catch (e) {
            // do nothing
        }
    }
    return st;
};

/**
 * The search state reducer. After every action, it removes unknown activity
 * tags (see `removeDisallowedActivityTags`). Every change to the search state goes through
 * here, including the search saved in local storage, which is loaded when the
 * store is created.
 */
export default function search(state, action) {
    const nextState = searchCriteria(state, action);
    return removeDisallowedActivityTags(nextState);
}

function searchCriteria(state = INITIAL_STATE, action) {
    let st = state;

    switch (action.type) {
        case types.UPDATE_CHECKIN_DATE:
            st = cloneState(state);
            st.checkin_time = action.time;
            delete st.err;
            return setStorage(st);

        case types.UPDATE_CHECKOUT_DATE:
            st = cloneState(state);
            st.checkout_time = action.time;
            delete st.err;
            return setStorage(st);

        case types.UPDATE_SEARCH_CRITERIA:
            st = cloneState(state);
            st[action.name] = action.value;
            return setStorage(st);

        case types.UPDATE_SEARCH_CRITERIAS:
            st = cloneState(state);
            st = { ...st, ...action };
            return setStorage(st);

        case types.ADD_SEARCH_FILTER:
            st = cloneState(state);
            st = {
                ...st,
                ...addUiFilter(st, action.uiFilterName, action.uiFilterOption),
                start: 0,
            };
            return setStorage(st);

        case types.SET_SEARCH_FILTER:
            st = cloneState(state);
            st = {
                ...st,
                ...setUiFilter(st, action.uiFilterName, action.uiFilterOption),
                start: 0,
            };
            return setStorage(st);

        case types.REMOVE_SEARCH_FILTER:
            st = cloneState(state);
            st = {
                ...st,
                ...removeUiFilter(st, action.uiFilterName, action.uiFilterOption),
                start: 0,
            };
            return setStorage(st);

        case types.RESET_SEARCH_FILTERS:
            st = cloneState(state);
            st.filters = [];
            st.include_unavailable = true;
            st.include_notreservable = true;
            st.include_partially_available = true;
            st.start = 0;
            st.uiFilters = createEmptyUiFilters();
            return setStorage(st);

        case types.SEND_SEARCH_RESULTS_REQUEST:
            if (state.aiSearch && state.aiSearch.criteriaKey !== action.criteriaKey) {
                st = cloneState(state);
                st.aiSearch = null;
                return setStorage(st);
            }
            return st;

        case types.RESET_SEARCH_FILTERS_CAMPSITES:
            st = cloneState(state);
            st.include_unavailable = true;
            st.include_notreservable = true;
            st.include_partially_available = true;
            st.start = 0;
            st.uiFilters.campsiteUseTypes = {};
            st.uiFilters.campsiteTypes = {};
            st.uiFilters.campsiteAmenities = {};
            st.uiFilters.campsiteElectricityHookup = {};
            st.uiFilters.campsiteVehicleLength = {};
            st.uiFilters.campsiteEquipment = {};
            st.uiFilters.occupants = {};
            st.uiFilters.reservable = {};
            return setStorage(st);

        default:
            return st;
    }
}

// cSpell:ignore placeid, checkin, notreservable, criterias
